import { useState, useEffect, useCallback } from 'react';
import { AuthVerificationCodeInput } from './AuthVerificationCodeInput';
import { AuthSubmitButton } from './AuthSubmitButton';
import type { EmailVerificationMethod } from '../../types';
import {
  VerificationStepWrapper,
  VerificationStepDescriptionContainer,
  VerificationStepDescriptionTitle,
  VerificationStepDescription,
  VerificationEmail,
  VerificationStepCodeContainer,
  VerificationStepCodeInputWrapper,
  VerificationStepResendContainer,
  VerificationStepResendButton,
} from '../forms/styled';

interface AuthResetPasswordVerificationStepProps {
  email: string;
  method: EmailVerificationMethod;
  onVerifyCode?: (code: string) => Promise<void>;
  onResendEmail: () => Promise<void>;
}

/**
 * Reset password verification step component
 *
 * Handles the verification flow for password reset:
 * - Shows appropriate UI based on method (code input or link message)
 * - Manages countdown timer for resend functionality
 * - Handles code verification for code method
 */
export function AuthResetPasswordVerificationStep({
  email,
  method,
  onVerifyCode,
  onResendEmail,
}: AuthResetPasswordVerificationStepProps) {
  const [resendDisabled, setResendDisabled] = useState(true);
  const [resendCountdown, setResendCountdown] = useState(60);
  const [isSending, setIsSending] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);

  const isLinkMethod = method === 'link';
  const displayDescription = isLinkMethod
    ? 'We have sent an email to {email}. Please check your email to reset your password. The link expires in 10 minutes.'
    : "We've sent a reset password code to your inbox at {email}. Enter it below to proceed.";

  // Countdown timer effect
  useEffect(() => {
    if (resendCountdown > 0) {
      const timer = setInterval(() => {
        setResendCountdown((prev) => {
          if (prev <= 1) {
            setResendDisabled(false);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [resendCountdown]);

  const handleResend = useCallback(async () => {
    setResendDisabled(true);
    setResendCountdown(60);
    setIsSending(true);

    try {
      await onResendEmail();
    } catch {
      // If resend fails, re-enable the button immediately
      setResendDisabled(false);
      setResendCountdown(0);
    } finally {
      setIsSending(false);
    }
  }, [onResendEmail]);

  const handleSubmit = async () => {
    if (!onVerifyCode || verificationCode.length !== 6) {
      return;
    }

    setIsVerifying(true);

    try {
      await onVerifyCode(verificationCode);
    } finally {
      setIsVerifying(false);
      setVerificationCode('');
    }
  };

  return (
    <VerificationStepWrapper>
      {/* Description with email (only for link method) */}
      {isLinkMethod && (
        <VerificationStepDescriptionContainer>
          <VerificationStepDescriptionTitle>Check Your Email</VerificationStepDescriptionTitle>
          <VerificationStepDescription>
            {displayDescription.split('{email}').map((part, index, array) => (
              <span key={index}>
                {part}
                {index < array.length - 1 && <VerificationEmail>{email}</VerificationEmail>}
              </span>
            ))}
          </VerificationStepDescription>
        </VerificationStepDescriptionContainer>
      )}

      {/* Code Input (only for code method) */}
      {!isLinkMethod && (
        <VerificationStepCodeContainer>
          <VerificationStepCodeInputWrapper>
            <AuthVerificationCodeInput
              value={verificationCode}
              onChange={setVerificationCode}
              disabled={isVerifying}
            />
          </VerificationStepCodeInputWrapper>

          <AuthSubmitButton
            type="button"
            isLoading={isVerifying}
            disabled={isVerifying || verificationCode.length !== 6}
            onClick={() => {
              void handleSubmit();
            }}
          >
            {isVerifying ? 'Verifying...' : 'Continue'}
          </AuthSubmitButton>
        </VerificationStepCodeContainer>
      )}

      {/* Resend Button */}
      <VerificationStepResendContainer>
        Didn&apos;t receive the email?{' '}
        <VerificationStepResendButton
          onClick={() => {
            void handleResend();
          }}
          disabled={resendDisabled || isSending}
        >
          {isSending
            ? 'Sending...'
            : resendDisabled
              ? `Retry in (${resendCountdown}s)`
              : 'Click to resend'}
        </VerificationStepResendButton>
      </VerificationStepResendContainer>
    </VerificationStepWrapper>
  );
}
